import {
  availabilitySearchAllHome,
  availabilitySearchAllModify,
  availabilitySellTripSelectionSuccess,
} from '@actions/availability.action';
import {
  BookingActionTypes,
  bookingGetBookingAfterPassengerUpdateSuccess,
  bookingGetBookingSuccess,
  bookingRetrieveBookingSuccess,
  bookingStoreBooking,
} from '@actions/booking.action';
import {
  loadSeatMapsSuccess,
  passengerSeatRemoved,
  passengerSeatSelected,
  setActivePassenger,
  setActiveSegment,
  sitTogetherSelected,
} from '@actions/seat.action';
import { SelectedSeats } from '@models/app.models';
import { Action, createReducer, on } from '@ngrx/store';
import { SeatMapAvailability } from 'src/app/models/booking.models';
import { autoAssignSeats } from "@actions/passenger.action";
import { N0PassengerSsrsUpdate, N0SsrPassengerUpdateRequest } from '@models/requests/ssr-passenger-update-request.model';

export interface State {
  activePassenger: string | null;
  activeSegment: string | null;
  seatMaps: SeatMapAvailability[] | null;
  selections: {
    seats: SelectedSeats;
    ssrs: N0SsrPassengerUpdateRequest[] | null;
  };
}

export const INITIAL_STATE: State = {
  seatMaps: null,
  activeSegment: null,
  activePassenger: null,
  selections: {
    seats: {},
    ssrs: null,
  },
};

const reducer = createReducer(
  INITIAL_STATE,

  on(
    setActiveSegment,
    (state, action): State => ({
      ...state,
      activeSegment: action.activeSegmentKey,
      activePassenger: action.activePassengerKey ?? state.activePassenger,
    }),
  ),

  on(
    setActivePassenger,
    (state, action): State => ({
      ...state,
      activePassenger: action.activePassengerKey ?? state.activePassenger,
    }),
  ),

  on(
    availabilitySellTripSelectionSuccess,
    (state): State => ({
      ...state,
      selections: {
        seats: {},
        ssrs: null,
      },
    }),
  ),

  on(
    bookingRetrieveBookingSuccess,
    (state): State => ({
      ...state,
      seatMaps: null,
    }),
  ),

  on(
    bookingStoreBooking,
    bookingGetBookingSuccess,
    bookingGetBookingAfterPassengerUpdateSuccess,
    bookingRetrieveBookingSuccess,
    (state, action): State => {
      const booking =
        action.type === BookingActionTypes.storeBooking
          ? action.payload
          : action.booking;
      return {
        ...state,
        activePassenger: booking.passengers?.[0]?.passengerKey ?? null,
        selections: {
          seats:
            booking.seats?.reduce((seatSelections, seat) => {
              return {
                ...seatSelections,
                [seat.segmentKey]: {
                  ...seat.passengerSeats.reduce(
                    (passengerSeatSelections, passengerSeat) => ({
                      ...passengerSeatSelections,
                      [passengerSeat.passengerKey]: {
                        seatKey: passengerSeat.unitKey,
                        journeyKey: passengerSeat.journeyKey,
                        seatDisplayName: passengerSeat.unitDesignator,
                      },
                    }),
                    {},
                  ),
                },
              };
            }, {}) ?? {},
          ssrs: booking.seats?.map((seat) => {
            return {
              journeyKey: seat.journeyKey,
              passengers: seat.seatSsr.map((paxSeat) => {
                return {
                  passengerKey: paxSeat.passengerKey,
                  ssrs: paxSeat.seats.map((ssr) => {
                    return {
                      ssrCode: ssr.ssrCode,
                      count: ssr.totalCount,
                    };
                  }),
                } as N0PassengerSsrsUpdate;
              }),
            };
          }) ?? state.selections.ssrs,
        },
      };
    },
  ),

  on(
    loadSeatMapsSuccess,
    autoAssignSeats,
    (state, action): State => ({
      ...state,
      seatMaps: action.seatMaps,
    }),
  ),

  on(
    availabilitySearchAllHome,
    availabilitySearchAllModify,
    availabilitySellTripSelectionSuccess,
    (state): State => ({
      ...state,
      seatMaps: null,
      selections: {
        seats: {},
        ssrs: null,
      },
    }),
  ),

  on(sitTogetherSelected, (state, action): State => {
    const newSsrs = state.selections.ssrs;
    if (!newSsrs) {
      return {
        ...state,
        selections: {
          ...state.selections,
          ssrs: [action.payload],
        }
      }
    }

    const journeySelection = newSsrs.find(
      (s) => s.journeyKey === action.payload.journeyKey,
    );

    if (!journeySelection) {
      return {
        ...state,
        selections: {
          ...state.selections,
          ssrs: newSsrs.concat(action.payload),
        }
      }
    }

    return {
      ...state,
      selections: {
        ...state.selections,
        ssrs: newSsrs.map((s) => {
          if (s.journeyKey !== action.payload.journeyKey || 
            !action.payload.passengers[0]
          ) {
            return s;
          }

          const passengerSelection = s.passengers.find(
            (p) => p.passengerKey === action.payload.passengers[0]!.passengerKey,
          );
          if (!passengerSelection) {
            return {
              ...s,
              passengers: s.passengers.concat(action.payload.passengers[0]),
            };
          }

          return {
            ...s,
            passengers: s.passengers.map((p) => {
              if (p.passengerKey !== action.payload.passengers[0]!.passengerKey) {
                return p;
              }
  
              const paxSsrs = p.ssrs;
  
              const ssrSelection = paxSsrs.find(
                (ssr) =>
                  ssr.ssrCode === action.payload.passengers[0]!.ssrs[0]?.ssrCode,
              );
              if (!ssrSelection && action.payload.passengers[0]!.ssrs[0]) {
                return {
                  ...p,
                  ssrs: paxSsrs.concat(action.payload.passengers[0]!.ssrs[0]),
                };
              }
  
              return {
                ...p,
                ssrs: paxSsrs.map((ssr) => {
                  if (
                    ssr.ssrCode !== action.payload.passengers[0]!.ssrs[0]?.ssrCode
                  ) {
                    return ssr;
                  }
  
                  return action.payload.passengers[0]!.ssrs[0];
                }),
              };
            }),
          };
        }),
      }
    }
  }),

  on(passengerSeatSelected, (currentState, action): State => {
    // if the seat has already been selected by someone on this segment, then don't allow
    // selecting it again for another passenger.
    const seatsOnSegment = Object.values(
      currentState.selections.seats[action.segmentKey] ?? [],
    ).map((seat) => seat.seatKey);
    if (seatsOnSegment.includes(action.seatKey)) {
      return currentState;
    }
    return {
      ...currentState,
      selections: {
        ...currentState.selections,
        seats: {
          ...currentState.selections.seats,
          [action.segmentKey]: {
            ...currentState.selections.seats[action.segmentKey],
            [action.passengerKey]: {
              journeyKey: action.journeyKey,
              seatKey: action.seatKey,
              seatDisplayName: action.seatDisplayName,
            },
          },
        },
      },
    };
  }),

  on(passengerSeatRemoved, (currentState, action): State => {
    const segmentSeats = {
      ...currentState.selections.seats[action.segmentKey],
    };
    delete segmentSeats[action.passengerKey];
    return {
      ...currentState,
      selections: {
        ...currentState.selections,
        seats: {
          ...currentState.selections.seats,
          [action.segmentKey]: {
            ...segmentSeats,
          },
        },
      },
    };
  }),
);

export const seatReducer = (state: State, action: Action): State =>
  reducer(state, action);
